import {
  Box,
  Flex,
  Text,
  Icon,
  FormControl,
  FormErrorMessage,
  Checkbox,
} from '@chakra-ui/react';
import { Trash01 } from '@kula-ai/icons/dual-tone/Trash01';
import { isInvalidField } from '@/lib/util';
import { renderErrors } from '@/ui/util';
import { SelectField } from '../../formBuilder/formFields/SelectField';
import { InputField } from '../../formBuilder/formFields/InputFields';
import { DateField } from '../../formBuilder/formFields/DateField';
import { UploadField } from '../../formBuilder/formFields/UploadField';

export const getOptions = (propSchema) => propSchema?.options?.entries || [];

export const getLabelKey = (propSchema) => {
  const entries = propSchema?.options?.entries;
  if (!entries || entries.length === 0) {
    if (propSchema?.label) return 'label';
    if (propSchema?.name) return 'name';
    if (propSchema?.display_full_name) return 'display_full_name';
    return 'label';
  }

  const firstEntry = entries[0];
  if (firstEntry?.label !== undefined) return 'label';
  if (firstEntry?.name !== undefined) return 'name';
  if (firstEntry?.display_full_name !== undefined) return 'display_full_name';
  return 'label';
};

export const getEmptyItem = (isPrimitiveArray, itemType, properties) => {
  if (isPrimitiveArray) {
    return itemType?.type === 'number' ? 0 : '';
  }
  return Object.fromEntries(
    Object.entries(properties).map(([k, v]) => [
      k,
      v.type === 'checkbox' ? false : '',
    ])
  );
};

// Check if max items limit is reached
export const isMaxItemsReached = (schema, itemsLength) =>
  schema?.max_items && itemsLength >= schema?.max_items;

// Validation function for primitive array fields
export const validatePrimitiveField = (value, itemType) => {
  if (itemType?.type === 'file') return undefined;

  if (itemType?.required && !value) {
    return `${itemType?.label || 'This field'} is required`;
  }

  if (!value) return undefined;

  if (itemType?.reg_exp) {
    const regex = new RegExp(itemType?.reg_exp);
    if (!regex.test(value)) {
      return `Invalid ${itemType?.label?.toLowerCase() || 'value'}`;
    }
  }

  if (itemType?.min_length && value.length < itemType?.min_length) {
    return `Minimum length is ${itemType?.min_length}`;
  }

  if (itemType?.max_length && value.length > itemType?.max_length) {
    return `Maximum length is ${itemType?.max_length}`;
  }

  return undefined;
};

// Field renderer for different types
export const renderFieldByType = (
  fieldType,
  fieldSchema,
  field,
  formik,
  itemType,
  schema
) => {
  switch (fieldType) {
    case 'file':
      return (
        <UploadField
          item={{
            name: field.name,
            maxFiles: 1,
            acceptedFiles:
              itemType?.file_restrictions?.accepted_mime_types?.join(',') || '',
            maxSize: itemType?.file_restrictions?.max_file_size,
            convertToBase64: true,
            returnFullFileObject: true,
            description: itemType?.description,
            gap: '3px',
          }}
          field={field}
        />
      );

    case 'single_select':
      return (
        <SelectField
          item={{
            name: field.name,
            options: getOptions(fieldSchema),
            placeholder: `Select ${fieldSchema?.label}`,
            label: fieldSchema?.label,
            valueKey: 'id',
            labelKey: getLabelKey(fieldSchema),
          }}
          field={field}
          formik={formik}
        />
      );

    case 'multi_select':
      return (
        <SelectField
          item={{
            name: field.name,
            options: getOptions(fieldSchema),
            placeholder: `Select ${fieldSchema?.label}`,
            label: fieldSchema?.label,
            valueKey: 'id',
            labelKey: getLabelKey(fieldSchema),
            isMulti: true,
          }}
          field={field}
          formik={formik}
        />
      );

    case 'number':
      return (
        <InputField
          item={{
            name: field.name,
            inputType: 'number',
            placeholder: fieldSchema?.label || schema?.label,
            label:
              fieldSchema?.label === itemType?.label
                ? null
                : fieldSchema?.label,
            isRequired: fieldSchema?.required,
          }}
          field={field}
          formik={formik}
        />
      );

    case 'date':
      return (
        <DateField
          item={{
            name: field.name,
            label: fieldSchema?.label,
            dateFormat: fieldSchema?.dateFormat,
            isRequired: fieldSchema?.required,
          }}
          field={field}
          formik={formik}
        />
      );

    case 'checkbox':
      return (
        <Checkbox {...field} isChecked={field.value}>
          {fieldSchema?.label}
        </Checkbox>
      );

    case 'text':
    default:
      return (
        <InputField
          item={{
            name: field.name,
            inputType: 'text',
            placeholder: fieldSchema?.label || schema?.label,
            label:
              fieldSchema?.label === itemType?.label
                ? null
                : fieldSchema?.label,
            isRequired: fieldSchema?.required,
            minLength: itemType?.min_length,
            maxLength: itemType?.max_length,
          }}
          field={field}
          formik={formik}
        />
      );
  }
};

export const FieldLabel = ({ label, required }) => (
  <Flex>
    <Text fontWeight="semibold" color="secondary-900" mb={1}>
      {label}
    </Text>
    {required && (
      <Text color="red" fontSize="12px" ml={1}>
        *
      </Text>
    )}
  </Flex>
);

export const ArrayItemContainer = ({ children, onRemove }) => (
  <Box
    border="1px solid"
    borderColor="secondary-200"
    borderRadius="md"
    px={5}
    py={6}
    mb={5}
    position="relative"
    bg="white"
  >
    <Icon
      as={Trash01}
      onClick={onRemove}
      aria-label="Delete"
      position="absolute"
      top={2}
      right={2}
      cursor="pointer"
    />
    {children}
  </Box>
);

export const MaxItemsMessage = ({ maxItems }) => (
  <Text fontSize="sm" color="secondary-600" mb={2}>
    {maxItems === 1
      ? 'Maximum limit of 1 item reached'
      : `Maximum limit of ${maxItems} items reached`}
  </Text>
);

export const ArrayHeader = ({ label, required, description }) => (
  <>
    <Flex gap={3} mb={2}>
      <Text fontWeight="bold" color="secondary-900" mb={2}>
        {label}
        {required && (
          <Text as="span" color="red" fontSize="12px" ml={1}>
            *
          </Text>
        )}
      </Text>
    </Flex>
    {description && (
      <Text fontSize="sm" color="secondary-600" mb={3}>
        {description}
      </Text>
    )}
  </>
);

export const FieldRenderer = ({
  field,
  form,
  meta,
  fieldSchema,
  formik,
  itemType,
  schema,
  isRequired,
}) => (
  <FormControl isInvalid={isInvalidField(form, meta)} isRequired={isRequired}>
    <Box>
      <Flex gap={3} mb={1}>
        <FieldLabel
          label={fieldSchema?.label}
          required={fieldSchema?.required || isRequired}
        />
      </Flex>
      {renderFieldByType(
        fieldSchema?.type,
        fieldSchema,
        field,
        formik,
        itemType,
        schema
      )}
      <FormErrorMessage flexDir="column" alignItems="flex-start">
        <Box>{renderErrors(field?.label, meta?.error)}</Box>
      </FormErrorMessage>
    </Box>
  </FormControl>
);
